import { useState, useEffect } from "react";
import { api } from "../api";
import { checkIsTablet } from "@helpers/is-tablet";

interface NetworkState {
  isOnline: boolean;
}

export const useOnlineStatus = () => {
  const [networkState, setNetworkState] = useState<NetworkState>({
    isOnline: navigator.onLine,
  });

  useEffect(() => {
    if (!checkIsTablet()) {
      return;
    }

    let intervalId: NodeJS.Timeout | null = null;

    const updateNetState = () => {
      const connection = (navigator as any).connection;
      if (connection) {
        setNetworkState({
          isOnline: navigator.onLine,
        });
      } else {
        setNetworkState((prev) => ({
          ...prev,
          isOnline: navigator.onLine,
        }));
      }
    };

    const checkConnection = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        await api.get("/api/health_check/", {
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
        setNetworkState((prev) => ({
          ...prev,
          isOnline: true,
        }));

        if (intervalId) {
          clearInterval(intervalId);
        }
        intervalId = setInterval(checkConnection, 30000);
      } catch {
        setNetworkState((prev) => ({
          ...prev,
          isOnline: false,
        }));

        if (intervalId) {
          clearInterval(intervalId);
        }
        intervalId = setInterval(checkConnection, 5000);
      }
    };

    const handleOnline = () => {
      updateNetState();
      checkConnection();
    };

    const handleOffline = () => {
      updateNetState();
      checkConnection();
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    checkConnection();

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  return networkState.isOnline;
};
